import Network from 'lib/api/network';

export default {
  namespaced: true,

  state() {
    return {
      listings: [],
      listingPresets: [],
      listingBeingEdited: {},
      pointsWallet: null,
      giftCardStatus: null,
    };
  },

  mutations: {
    update(state, { key, value }) {
      state[key] = value;
    },

    updateListingBeingEdited(state, { key, value }) {
      state.listingBeingEdited = {
        ...state.listingBeingEdited,
        [key]: value,
      };
    },

    updateListingBeingEditedImage(state, files) {
      state.listingBeingEdited = {
        ...state.listingBeingEdited,
        image: files[0],
      };
    },

    setListings(state, listings) {
      state.listings = [...listings];
    },

    setListing(state, listing) {
      const existingIndex = state.listings.findIndex(l => l.id === listing.id);
      const listings = [...state.listings];

      if (existingIndex >= 0) {
        listings.splice(existingIndex, 1, listing);
      } else {
        listings.push(listing);
      }

      state.listings = listings;
    },

    clearListing(state) {
      state.listing = null;
    },
  },

  actions: {
    upsertListing({ commit, dispatch }, listing) {
      const callbacks = {
        success: (response) => {
          commit('setListing', response.listing);
          commit('clearListing');
          dispatch('setSuccessToast', 'Your changes have been saved', { root: true });
        },
        error: () => {
          dispatch('setErrorToast', null, { root: true });
        },
      };
      if (listing.id) {
        Network.put(`/rewards/${listing.id}`, { listing }, callbacks);
      } else {
        Network.post('/rewards', { listing }, callbacks);
      }
    },

    handleGiftCardErrorModal({ rootState, commit }, { error, pointsConfig }) {
      if (error === 'upgrade_required') {
        window.open('/billing', '_blank');
      } else if (error === 'finish_trial' || error === 'blocked') {
        window.Intercom('showNewMessage');
      } else if (error === 'invoiced') {
        const { currentUser, currentTeam } = rootState;
        Network.post('/intercom_tickets', {
          ticket: {
            ticket_type: 'gift_cards',
            title: 'Invoiced Team Requesting Gift Card Rewards Access',
            description: [
              'Team with invoiced billing is requesting access to gift card rewards functionality.',
              '',
              'Requesting User:',
              `- Email: ${currentUser.email}`,
              `- User ID: ${currentUser.id}`,
              '',
              'Team Information:',
              `- Team ID: ${currentTeam.id}`,
              `- Team Name: ${currentTeam.name}`,
              '',
              'Config Information:',
              `- Points Config ID: ${pointsConfig.id}`,
            ].join('\n'),
          },
        }, {
          success: () => {
            commit('updateModal', {
              visible: true,
              subcomponent: 'shoutouts-rewards-modals-gift-card-request-submitted',
              size: 'xl',
            }, { root: true });
          },
          error: () => {
            // Fallback to Intercom or email support if ticket creation fails
            if (window.Intercom?.booted) {
              window.Intercom('showNewMessage');
            } else {
              commit('updateModal', {
                visible: true,
                subcomponent: 'donut-modal-email-support',
              }, { root: true });
            }
          },
        });
      } else if (window.Intercom?.booted) {
        window.Intercom('showNewMessage');
      } else {
        commit('updateModal', {
          visible: true,
          subcomponent: 'donut-modal-email-support',
        }, { root: true });
      }
    },

    saveOrUpdateListing({ dispatch }, listing) {
      if (listing.image) {
        listing.image_url = listing.image.url;
        listing.image_id = listing.image.id;
        listing.image = null;
      }

      dispatch('upsertListing', listing);
    },

    initializeListingEdit({ commit }, listing) {
      commit('update', { key: 'listingBeingEdited', value: listing });
    },

    openConfigureModal({ state, commit }, { listing, preset, pointsConfig, dollarsPerPoint }) {
      if (!listing.id) {
        state.listingBeingEdited = {
          name: null,
          short_description: null,
          description: null,
          points_cost: null,
          quantity_available: null,
          state: 'active',
        };
      }
      commit('uploads/setFiles', [], { root: true });

      commit('updateModal', {
        visible: true,
        subcomponent: 'shoutouts-rewards-modals-configure',
        data: { listing, preset, pointsConfig, dollarsPerPoint },
        size: 'xl',
        dismissable: false,
      }, { root: true });
    },

    confirmGiftCardRedemption({ commit }, { gift_card_points_cost }) {
      commit('updateModal', {
        visible: true,
        subcomponent: 'shoutouts-rewards-modals-confirm-gift-card-redemption',
        data: { gift_card_points_cost },
      }, { root: true });
    },

    redeemListing({ commit, dispatch, state }, { listing, pointsConfig, gift_card_points_cost, cents_per_point }) {
      if (state.pointsWallet.current_redeemable < listing.points_cost) {
        dispatch('setErrorToast', `You don't have enough ${pointsConfig.points_name_plural} to redeem this.`, { root: true });
        return;
      }

      Network.post(`/rewards/${listing.id}/redeem`, { gift_card_points_cost, cents_per_point }, {
        success: (response) => {
          commit('startConfetti', null, { root: true });
          commit('setListings', response.listings);
          commit('update', { key: 'pointsWallet', value: response.points_wallet });
          commit('updateModal', {
            visible: true,
            subcomponent: 'shoutouts-rewards-modals-redemption-success',
            ...(listing.autoredeemable ? { dismissable: false } : {}),
            data: {
              listing,
              pointsConfig,
              gift_card_link: response.gift_card_link,
              gift_card_points_cost: response.gift_card_points_cost,
            },
          }, { root: true });
        },
        error: (errorResponse) => {
          dispatch('setErrorToast', errorResponse.responseJSON.error, { root: true });
          commit('closeModal', null, { root: true });
          commit('setListings', errorResponse.responseJSON.listings);
        },
      });
    },

    updateGiftCardListing({ commit, dispatch }, { listing, pointsConfig, successMessage, errorMessage }) {
      commit('update', { key: 'formSubmitted', value: true }, { root: true });

      const callbacks = {
        success: (response) => {
          commit('setListing', response.listing);
          dispatch('setSuccessToast', successMessage, { root: true });
        },
        error: (errorResponse) => {
          if (errorResponse.responseJSON?.error) {
            dispatch('handleGiftCardErrorModal', { pointsConfig, error: errorResponse.responseJSON.error });
          } else {
            dispatch('setErrorToast', errorMessage, { root: true });
          }
        },
        complete: () => {
          commit('update', { key: 'formSubmitted', value: false }, { root: true });
        },
      };

      if (listing.id) {
        Network.put(`/rewards/${listing.id}`, { listing }, callbacks);
      } else {
        Network.post('/rewards', { listing }, callbacks);
      }
    },

    confirmGiftCardToggle({ dispatch, state }, { pointsConfig }) {
      const { listings, listingPresets } = state;
      const existingListing = listings.find(l => l.is_gift_card);

      let listing;

      if (existingListing) {
        // For existing listings, only update the state
        listing = {
          id: existingListing.id,
          state: 'active',
        };
      } else {
        // For new listings, use preset data
        const giftCardPreset = listingPresets.find(preset => preset.is_gift_card === true);
        listing = {
          name: giftCardPreset.name,
          short_description: giftCardPreset.short_description,
          description: giftCardPreset.description,
          preset_id: giftCardPreset.id,
          is_gift_card: true,
          state: 'active',
        };
      }

      dispatch('updateGiftCardListing', {
        listing,
        pointsConfig,
        successMessage: 'Gift cards enabled',
        errorMessage: 'There was an error enabling gift cards',
      });
    },

    toggleGiftCards({ commit, dispatch, state }, { value, pointsConfig, dollarsPerPoint }) {
      const { listings } = state;
      const existingListing = listings.find(l => l.is_gift_card);

      // When disabling, just update the existing listing to inactive
      if (!value && existingListing) {
        const listing = {
          id: existingListing.id,
          state: 'inactive',
        };

        dispatch('updateGiftCardListing', {
          listing,
          pointsConfig,
          successMessage: 'Gift cards disabled',
          errorMessage: 'There was an error disabling gift cards',
        });
        return;
      }

      // When enabling, always show confirmation modal
      if (value) {
        commit('updateModal', {
          visible: true,
          subcomponent: 'shoutouts-programs-modals-gift-card-toggle-confirmation',
          data: { pointsConfig, dollarsPerPoint },
          size: 'lg',
        }, { root: true });
      }
    },
  },

  getters: {
    convertGiftCardPointsToDollars: (_state) => (listing, points) => listing.gift_card_options?.find(({ points: p }) => p === points)?.dollars,
  },
};
